import type { CsvTemplateColumn } from '../components/ui/CsvTemplateHint';

const BOM = '﻿';

const csvCell = (value: string): string =>
  /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/**
 * A CSV template as text: header row, then the example rows. A cell is quoted
 * when it holds a comma, quote or newline; lines end `\r\n` and the text starts
 * with a UTF-8 byte-order mark so Excel opens accented names correctly.
 * Throws when an example row is not as long as the header row.
 */
export function buildCsvTemplate(columns: ReadonlyArray<Pick<CsvTemplateColumn, 'name'>>, exampleRows: ReadonlyArray<ReadonlyArray<string>>): string {
  exampleRows.forEach((row, index) => {
    if (row.length !== columns.length) {
      throw new Error(`Example row ${index + 1} has ${row.length} cells but the template has ${columns.length} columns`);
    }
  });
  const lines = [columns.map((column) => column.name), ...exampleRows].map((row) => row.map(csvCell).join(','));
  return BOM + lines.join('\r\n') + '\r\n';
}

type ColumnMeta = Omit<CsvTemplateColumn, 'name' | 'example'>;

/**
 * Column definitions for the explainer, in header order, from a shared template
 * fixture. Each column's example is the first non-empty value in the example
 * rows. A header with no entry in `meta` throws, so a column added to the
 * fixture cannot ship undocumented.
 */
export function templateColumns(
  headers: ReadonlyArray<string>,
  exampleRows: ReadonlyArray<ReadonlyArray<string>>,
  meta: Record<string, ColumnMeta>,
): CsvTemplateColumn[] {
  return headers.map((name, index) => {
    const entry = meta[name];
    if (!entry) throw new Error(`No description for template column "${name}"`);
    const example = exampleRows.map((row) => row[index]).find((value) => value !== undefined && value !== '');
    return { name, ...entry, ...(example ? { example } : {}) };
  });
}

/** The header row of a CSV file's text, lower-cased, with a BOM and quotes removed. */
const headerCells = (text: string): string[] =>
  (text.replace(/^﻿/, '').split(/\r?\n/, 1)[0] || '')
    .split(',')
    .map((cell) => cell.trim().replace(/^"|"$/g, '').toLowerCase());

/** True when the file's first line is exactly `headers`, so the sheet is in the canonical template layout. */
export const hasTemplateHeader = (text: string, headers: ReadonlyArray<string>): boolean => {
  const found = headerCells(text);
  const wanted = headers.map((header) => header.toLowerCase());
  return found.length === wanted.length && wanted.every((header, index) => found[index] === header);
};

/** A blob's text through FileReader, which every browser (and jsdom) has. */
const readText = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });

/**
 * `'motzu'` when `file` is a CSV whose header row is exactly `headers`, else
 * `undefined`. The pricing uploads send it as `?template=motzu` so a sheet in
 * the downloadable template layout is read by the canonical parser (brand,
 * rebate window and all) instead of being guessed at as a vendor sheet. Any
 * other file, and any read failure, keeps the upload exactly as it was.
 */
export async function canonicalSheetTemplateParam(file: File, headers: ReadonlyArray<string>): Promise<'motzu' | undefined> {
  if (!/\.csv$/i.test(file.name)) return undefined;
  try {
    const head = await readText(file.slice(0, 2048));
    return hasTemplateHeader(head, headers) ? 'motzu' : undefined;
  } catch {
    return undefined;
  }
}
